import { useQuery } from "@tanstack/react-query";

import apiClient from "../apiClient";

export type AnalyticsDay = string;
export type AnalyticsWindowDays = 7 | 30 | 90;

export type FeedObject = Record<string, unknown>;
export type FeedEpisodeObject = Record<string, unknown>;
export type ListenerObject = Record<string, unknown>;

export type AnalyticsPagination = {
  total_items: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  links: {
    next: string | null;
    prev: string | null;
  };
};

export type FeedAnalyticsBaseParams = {
  feedId: string;
  day: AnalyticsDay;
};

export type FeedEpisodeAnalyticsBaseParams = FeedAnalyticsBaseParams & {
  feedEpisodeId: string;
};

export type PaginatedAnalyticsParams = {
  page?: number;
  limit?: number;
};

export type FeedAnalyticsQueryParams = FeedAnalyticsBaseParams;

export type FeedEpisodeAnalyticsQueryParams = FeedEpisodeAnalyticsBaseParams;

export type FeedListenersQueryParams = FeedAnalyticsBaseParams & PaginatedAnalyticsParams;

export type FeedEpisodeListenersQueryParams = FeedEpisodeAnalyticsBaseParams &
  PaginatedAnalyticsParams;

export type FeedSnapshotQueryParams = {
  feedId?: string;
  feedCategoryId?: string[];
  episodeTitle?: string;
  windowDays: AnalyticsWindowDays;
};

export type FeedSubscriberTrendsQueryParams = Omit<FeedSnapshotQueryParams, "episodeTitle">;

export type FeedDownloadTrendsQueryParams = {
  feedId?: string;
  feedCategoryId?: string[];
  episodeTitle?: string;
  startDate: AnalyticsDay;
  endDate: AnalyticsDay;
  granularity: "day";
};

export type FeedSubscriptionFunnelQueryParams = {
  feedId?: string;
  feedCategoryId?: string[];
  episodeTitle?: string;
  startDate: string;
  endDate: string;
};

export type FeedEpisodeSummaryQueryParams = {
  feedId?: string;
  feedCategoryId?: string[];
  episodeTitle?: string;
  windowDays: AnalyticsWindowDays;
  limit?: number;
};

export type FeedEpisodeDetailsQueryParams = {
  feedId: string;
  feedEpisodeId: string;
  windowDays: AnalyticsWindowDays;
};

export type FeedTypePerformanceQueryParams = {
  feedCategoryId?: string[];
  windowDays: AnalyticsWindowDays;
};

export type FeedDayRollup = {
  local_user_id: string;
  feed_id: string;
  day: string;
  downloads_equivalent: number;
  download_events: number;
  engaged_listeners: number;
  updated_at: string;
};

export type EpisodeDayRollup = {
  local_user_id: string;
  feed_id: string;
  feed_episode_id: string;
  day: string;
  downloads_equivalent: number;
  download_events: number;
  engaged_listeners: number;
  updated_at: string;
};

export type FeedActiveListenerSnapshotDayRollup = {
  local_user_id: string;
  feed_id: string;
  day: string;
  window_days: AnalyticsWindowDays;
  active_listeners_total: number;
  inactive_listeners_total: number;
  churned_listeners_total: number;
  updated_at: string;
};

export type FeedAnalyticsResponse = {
  data: FeedDayRollup & {
    feed: FeedObject;
  };
};

export type FeedEpisodeAnalyticsResponse = {
  data: EpisodeDayRollup & {
    feed: FeedObject;
    feed_episode: FeedEpisodeObject;
  };
};

export type FeedListenersResponse = {
  data: {
    feed: FeedObject;
    listeners: ListenerObject[];
  };
  pagination: AnalyticsPagination;
};

export type FeedEpisodeListenersResponse = {
  data: {
    feed: FeedObject;
    feed_episode: FeedEpisodeObject;
    listeners: ListenerObject[];
  };
  pagination: AnalyticsPagination;
};

export type FeedSnapshotResponse = {
  data: Pick<
    FeedActiveListenerSnapshotDayRollup,
    | "day"
    | "window_days"
    | "active_listeners_total"
    | "inactive_listeners_total"
    | "churned_listeners_total"
  > | null;
};

export type FeedDownloadTrend = {
  date: AnalyticsDay;
  downloads: number;
  unique_listeners: number;
};

export type FeedDownloadTrendsResponse = {
  data: FeedDownloadTrend[];
};

export type FeedTotalDownloadsResponse = {
  data: {
    downloads: number;
  };
};

export type FeedSubscriberTrend = {
  date: AnalyticsDay;
  total_subscribers: number;
};

export type FeedSubscriberTrendsResponse = {
  data: FeedSubscriberTrend[];
  window_days: AnalyticsWindowDays;
};

export type FeedSubscriptionFunnelResponse = {
  data: {
    activated: {
      count: number;
    };
  };
};

export type FeedEpisodeSummary = {
  feed_id: string;
  feed_episode_id: string;
  title: string;
  publish_date: string;
  feed_display_type: string;
  total_downloads: number;
  unique_listeners: number;
  estimated_completion_rate: number;
};

export type FeedEpisodeSummaryResponse = AnalyticsPagination & {
  data: FeedEpisodeSummary[];
  average_completion_rate: number;
};

export type FeedEpisodeCompletionDistribution = {
  progress_percentage: number;
  listener_count: number;
  listener_percentage: number;
};

export type FeedEpisodeDetails = {
  feed_id: string;
  feed_episode_id: string;
  title: string;
  window_days: AnalyticsWindowDays;
  downloads: number;
  engaged_listeners: number;
  average_completion_rate: number;
  average_listen_seconds: number;
  duration_seconds: number;
  completion_distribution: FeedEpisodeCompletionDistribution[];
};

export type FeedEpisodeDetailsResponse = {
  data: FeedEpisodeDetails;
};

export type FeedTypePerformance = {
  feed_type: string;
  subscribers: number;
  average_completion_rate: number;
};

export type FeedTypePerformanceResponse = {
  data: FeedTypePerformance[];
  window_days: AnalyticsWindowDays;
};

type QueryParamValue = string | number | string[] | number[] | undefined;

const createQueryString = (params: Record<string, QueryParamValue>) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(`${key}[]`, String(item)));
    } else if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
};

const fetchAnalytics = async <TResponse>(path: string, params: Record<string, QueryParamValue>) => {
  const queryString = createQueryString(params);
  const response = await apiClient.get(`${path}?${queryString}`);

  if (!response.ok) {
    throw new Error("Request failed!");
  }

  return (await response.json()) as TResponse;
};

export const getFeedDay = async (
  params: FeedAnalyticsQueryParams,
): Promise<FeedAnalyticsResponse> =>
  fetchAnalytics<FeedAnalyticsResponse>("/services/analytics/feed", params);

export const getFeedEpisodeDay = async (
  params: FeedEpisodeAnalyticsQueryParams,
): Promise<FeedEpisodeAnalyticsResponse> =>
  fetchAnalytics<FeedEpisodeAnalyticsResponse>("/services/analytics/feed/episode", params);

export const getFeedListeners = async (
  params: FeedListenersQueryParams,
): Promise<FeedListenersResponse> =>
  fetchAnalytics<FeedListenersResponse>("/services/analytics/feed/listeners", params);

export const getFeedEpisodeListeners = async (
  params: FeedEpisodeListenersQueryParams,
): Promise<FeedEpisodeListenersResponse> =>
  fetchAnalytics<FeedEpisodeListenersResponse>(
    "/services/analytics/feed/episode/listeners",
    params,
  );

export const getFeedSnapshot = async (
  params: FeedSnapshotQueryParams,
): Promise<FeedSnapshotResponse> =>
  fetchAnalytics<FeedSnapshotResponse>("/services/analytics/feed/snapshot", params);

export const getFeedDownloadTrends = async (
  params: FeedDownloadTrendsQueryParams,
): Promise<FeedDownloadTrendsResponse> =>
  fetchAnalytics<FeedDownloadTrendsResponse>("/services/analytics/feed/trends/downloads", params);

export const getFeedTotalDownloads = async (feedId?: string): Promise<FeedTotalDownloadsResponse> =>
  fetchAnalytics<FeedTotalDownloadsResponse>("/services/analytics/feed/downloads", { feedId });

export const getFeedSubscriberTrends = async (
  params: FeedSubscriberTrendsQueryParams,
): Promise<FeedSubscriberTrendsResponse> =>
  fetchAnalytics<FeedSubscriberTrendsResponse>(
    "/services/analytics/feed/trends/subscribers",
    params,
  );

export const getFeedSubscriptionFunnel = async (
  params: FeedSubscriptionFunnelQueryParams,
): Promise<FeedSubscriptionFunnelResponse> =>
  fetchAnalytics<FeedSubscriptionFunnelResponse>(
    "/services/analytics/feed/subscription-funnel",
    params,
  );

export const getFeedEpisodeSummary = async (
  params: FeedEpisodeSummaryQueryParams,
): Promise<FeedEpisodeSummaryResponse> =>
  fetchAnalytics<FeedEpisodeSummaryResponse>("/services/analytics/feed/episode/summary", params);

export const getFeedEpisodeDetails = async (
  params: FeedEpisodeDetailsQueryParams,
): Promise<FeedEpisodeDetailsResponse> =>
  fetchAnalytics<FeedEpisodeDetailsResponse>("/services/analytics/feed/episode/details", params);

export const getFeedTypePerformance = async (
  params: FeedTypePerformanceQueryParams,
): Promise<FeedTypePerformanceResponse> =>
  fetchAnalytics<FeedTypePerformanceResponse>("/services/analytics/feed/type-performance", params);

export const useFeedSnapshotQuery = (
  feedId: string | undefined,
  feedCategoryIds: string[],
  windowDays: AnalyticsWindowDays,
  episodeTitle: string | undefined,
  enabled = true,
) =>
  useQuery(
    ["feedSnapshot", feedId, feedCategoryIds, windowDays, episodeTitle],
    async () =>
      await getFeedSnapshot({ feedId, feedCategoryId: feedCategoryIds, episodeTitle, windowDays }),
    { enabled, suspense: false },
  );

export const useFeedDownloadTrendsQuery = (
  feedId: string | undefined,
  feedCategoryIds: string[],
  startDate: string,
  endDate: string,
  episodeTitle: string | undefined,
  enabled = true,
) =>
  useQuery(
    ["feedDownloadTrends", feedId, feedCategoryIds, startDate, endDate, episodeTitle],
    async () =>
      await getFeedDownloadTrends({
        feedId,
        feedCategoryId: feedCategoryIds,
        startDate,
        endDate,
        episodeTitle,
        granularity: "day",
      }),
    { enabled, suspense: false },
  );

export const useFeedTotalDownloadsQuery = (feedId: string | undefined, enabled = true) =>
  useQuery(["feedTotalDownloads", feedId], async () => await getFeedTotalDownloads(feedId), {
    enabled,
    suspense: false,
  });

export const useFeedSubscriberTrendsQuery = (
  feedId: string | undefined,
  feedCategoryIds: string[],
  windowDays: AnalyticsWindowDays,
  enabled = true,
) =>
  useQuery(
    ["feedSubscriberTrends", feedId, feedCategoryIds, windowDays],
    async () =>
      await getFeedSubscriberTrends({ feedId, feedCategoryId: feedCategoryIds, windowDays }),
    { enabled, suspense: false },
  );

export const useFeedSubscriptionFunnelQuery = (
  feedId: string | undefined,
  feedCategoryIds: string[],
  startDate: string,
  endDate: string,
  episodeTitle: string | undefined,
  enabled = true,
) =>
  useQuery(
    ["feedSubscriptionFunnel", feedId, feedCategoryIds, startDate, endDate, episodeTitle],
    async () =>
      await getFeedSubscriptionFunnel({
        feedId,
        feedCategoryId: feedCategoryIds,
        startDate,
        endDate,
        episodeTitle,
      }),
    { enabled, suspense: false },
  );

export const useFeedEpisodeSummaryQuery = (
  feedId: string | undefined,
  feedCategoryIds: string[],
  windowDays: AnalyticsWindowDays,
  episodeTitle: string | undefined,
  enabled = true,
) =>
  useQuery(
    ["feedEpisodeSummary", feedId, feedCategoryIds, windowDays, episodeTitle],
    async () =>
      await getFeedEpisodeSummary({
        feedId,
        feedCategoryId: feedCategoryIds,
        episodeTitle,
        windowDays,
        limit: 100,
      }),
    { enabled, suspense: false },
  );

export const useFeedEpisodeDetailsQuery = (
  feedId: string | undefined,
  feedEpisodeId: string | undefined,
  windowDays: AnalyticsWindowDays,
) =>
  useQuery(
    ["feedEpisodeDetails", feedId, feedEpisodeId, windowDays],
    async () =>
      await getFeedEpisodeDetails({
        feedId: feedId as string,
        feedEpisodeId: feedEpisodeId as string,
        windowDays,
      }),
    // Render the detail modal while its request is pending instead of suspending the stats page.
    { enabled: Boolean(feedId && feedEpisodeId), suspense: false },
  );

export const useFeedTypePerformanceQuery = (
  feedCategoryIds: string[],
  windowDays: AnalyticsWindowDays,
  enabled = true,
) =>
  useQuery(
    ["feedTypePerformance", feedCategoryIds, windowDays],
    async () => await getFeedTypePerformance({ feedCategoryId: feedCategoryIds, windowDays }),
    { enabled, suspense: false },
  );
